<script lang="ts">
  import Widget from '../widgets/index.svelte'
  import { detectFieldType, type FieldType } from '../widgets/detect'

  type Entry = {
    key: string
    value: unknown
  }

  type ParsedState =
    | {
        ok: true
        value: Record<string, unknown>
      }
    | {
        ok: false
        error: string
      }

  const titleFields = ['title', 'report_title', 'name', 'report_id', 'id']
  const descriptionFields = ['description', 'summary', 'subtitle', 'overview', 'details']
  const metadataSlots = [
    { label: 'Status', keys: ['status', 'state', 'inspection_status'] },
    { label: 'Priority', keys: ['priority', 'severity', 'urgency'] },
    { label: 'Next step', keys: ['next_step', 'next_action', 'recommended_next_step', 'action_required'] },
    { label: 'Report ID', keys: ['report_id', 'id', 'ticket_id', 'case_id', 'inspection_id'] },
  ]
  const aboutPriority = [
    'customer',
    'client',
    'contact',
    'account',
    'patient',
    'vehicle',
    'project',
    'property',
    'asset',
    'site',
    'location',
  ]
  const findingNames = ['findings', 'issues', 'inspection', 'results', 'observations']
  const actionNames = ['recommended_actions', 'recommendations', 'actions', 'next_actions']
  const widgetTypes = new Set<FieldType>(['date', 'email', 'url', 'phone', 'status', 'bool', 'null', 'id', 'count'])

  let {
    value,
  }: {
    value: unknown
  } = $props()

  const parsedState = $derived(parseValue(value))
  const report = $derived(parsedState.ok ? parsedState.value : undefined)
  const title = $derived(report ? getTitle(report) : 'Service Report')
  const subtitle = $derived(report ? getSubtitle(report) : '')
  const metadata = $derived(report ? getMetadata(report) : [])
  const aboutSections = $derived(report ? getAboutSections(report) : [])
  const findings = $derived(report ? getObjectArray(report, findingNames) : [])
  const actions = $derived(report ? getObjectArray(report, actionNames) : [])

  function parseValue(input: unknown): ParsedState {
    if (typeof input === 'string') {
      try {
        const parsed = JSON.parse(input)

        if (isPlainObject(parsed)) {
          return { ok: true, value: parsed }
        }

        return { ok: false, error: 'Report View expects a JSON object' }
      } catch (error) {
        return {
          ok: false,
          error: error instanceof Error ? error.message : 'Unable to parse JSON',
        }
      }
    }

    if (isPlainObject(input)) {
      return { ok: true, value: input }
    }

    return { ok: false, error: 'Report View expects a JSON object' }
  }

  function isPlainObject(input: unknown): input is Record<string, unknown> {
    return typeof input === 'object' && input !== null && !Array.isArray(input)
  }

  function isScalar(input: unknown): boolean {
    return input === null || ['string', 'number', 'boolean'].includes(typeof input)
  }

  function getTitle(input: Record<string, unknown>): string {
    for (const field of titleFields) {
      const match = Object.entries(input).find(([key, entryValue]) => {
        return key.toLowerCase() === field && isScalar(entryValue) && entryValue !== null
      })

      if (match) {
        return field.includes('report_id') ? 'Service Report' : formatPrimitive(match[1])
      }
    }

    return 'Service Report'
  }

  function getSubtitle(input: Record<string, unknown>): string {
    for (const field of descriptionFields) {
      const match = Object.entries(input).find(([key, entryValue]) => {
        return key.toLowerCase() === field && typeof entryValue === 'string' && entryValue.trim()
      })

      if (match && typeof match[1] === 'string') {
        return match[1]
      }
    }

    const createdAt = getFirstValue(input, ['created_at', 'created', 'date'])
    const reportId = getFirstValue(input, ['report_id', 'id'])
    const shop = isPlainObject(input.shop) ? getFirstValue(input.shop, ['name']) : undefined

    return [shop, reportId ? `Report ${formatPrimitive(reportId)}` : '', createdAt ? formatDateValue(createdAt) : '']
      .filter(Boolean)
      .join(' / ')
  }

  function getMetadata(input: Record<string, unknown>): Entry[] {
    return metadataSlots.map((slot) => {
      const value = getFirstValue(input, slot.keys)
      return { key: slot.label, value: value ?? null }
    })
  }

  function getFirstValue(input: Record<string, unknown>, keys: string[]): unknown {
    for (const candidate of keys) {
      for (const [key, entryValue] of Object.entries(input)) {
        if (key.toLowerCase() === candidate && isScalar(entryValue) && entryValue !== null) {
          return entryValue
        }
      }
    }

    return undefined
  }

  function getAboutSections(input: Record<string, unknown>): Entry[] {
    const sections = Object.entries(input)
      .filter(([, entryValue]) => isPlainObject(entryValue))
      .map(([key, entryValue]) => ({ key, value: entryValue }))

    return sections
      .sort((left, right) => getAboutRank(left.key) - getAboutRank(right.key))
      .slice(0, 2)
  }

  function getAboutRank(key: string): number {
    const normalizedKey = key.toLowerCase()
    const priorityIndex = aboutPriority.findIndex((candidate) => normalizedKey.includes(candidate))
    return priorityIndex === -1 ? 100 : priorityIndex
  }

  function getObjectArray(input: Record<string, unknown>, names: string[]): Record<string, unknown>[] {
    const nameSet = new Set(names)

    for (const [key, entryValue] of Object.entries(input)) {
      if (!nameSet.has(key.toLowerCase()) || !Array.isArray(entryValue)) {
        continue
      }

      return entryValue.filter(isPlainObject)
    }

    return []
  }

  function getFindingTitle(input: Record<string, unknown>): string {
    return formatPrimitive(getFirstValue(input, ['title', 'name', 'category', 'id']) ?? 'Finding')
  }

  function getFindingBody(input: Record<string, unknown>): string {
    return formatPrimitive(
      getFirstValue(input, ['details', 'description', 'summary', 'recommendation', 'body']) ?? '',
    )
  }

  function getSeverity(input: Record<string, unknown>): string {
    return formatPrimitive(getFirstValue(input, ['severity', 'priority', 'status']) ?? 'Info')
  }

  function getActionTitle(input: Record<string, unknown>): string {
    return formatPrimitive(getFirstValue(input, ['action', 'title', 'name', 'recommendation']) ?? 'Recommended action')
  }

  function getPriority(input: Record<string, unknown>): string {
    return formatPrimitive(getFirstValue(input, ['priority', 'severity', 'status']) ?? 'Medium')
  }

  function labelize(key: string): string {
    return key
      .replaceAll('_', ' ')
      .replaceAll('-', ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase())
  }

  function formatDateValue(input: unknown): string {
    if (typeof input !== 'string' || Number.isNaN(Date.parse(input))) {
      return formatPrimitive(input)
    }

    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(new Date(input))
  }

  function formatPrimitive(input: unknown): string {
    if (input === null) {
      return 'Not provided'
    }

    if (typeof input === 'boolean') {
      return input ? 'Yes' : 'No'
    }

    return String(input)
  }
</script>

{#snippet valueCell(entry: Entry)}
  {@const fieldType = detectFieldType(entry.key, entry.value)}
  {#if widgetTypes.has(fieldType)}
    <Widget type={fieldType} value={entry.value} />
  {:else}
    <span>{formatPrimitive(entry.value)}</span>
  {/if}
{/snippet}

{#if !parsedState.ok}
  <div class="report-view">
    <section class="report-card">
      <h2>Invalid report</h2>
      <p>{parsedState.error}</p>
    </section>
  </div>
{:else if report}
  <div class="report-view">
    <header class="report-header">
      <span class="report-eyebrow">Report</span>
      <h2 class="report-title">{title}</h2>
      {#if subtitle}
        <p>{subtitle}</p>
      {/if}
    </header>

    <div class="report-metadata-grid" aria-label="Report metadata">
      {#each metadata as entry (entry.key)}
        <section class="report-metadata-card">
          <span>{entry.key}</span>
          <strong>{@render valueCell(entry)}</strong>
        </section>
      {/each}
    </div>

    {#if aboutSections.length > 0}
      <div class="report-about-grid">
        {#each aboutSections as section (section.key)}
          {#if isPlainObject(section.value)}
            <section class="report-card">
              <h3>{labelize(section.key)}</h3>
              <div>
                {#each Object.entries(section.value) as [key, entryValue] (key)}
                  <div class="report-card-row">
                    <span>{labelize(key)}</span>
                    {@render valueCell({ key, value: entryValue })}
                  </div>
                {/each}
              </div>
            </section>
          {/if}
        {/each}
      </div>
    {/if}

    {#if findings.length > 0}
      <section class="report-card">
        <h3>Findings</h3>
        <div class="report-findings-list">
          {#each findings as finding, index (`finding-${index}`)}
            {@const severity = getSeverity(finding)}
            <article class="report-finding-row">
              <span class="report-finding-number">{index + 1}</span>
              <div>
                <div class="report-finding-heading">
                  <Widget type="status" value={severity} />
                  <strong>{getFindingTitle(finding)}</strong>
                </div>
                <p>{getFindingBody(finding)}</p>
              </div>
              <button type="button">Update status</button>
            </article>
          {/each}
        </div>
      </section>
    {/if}

    {#if actions.length > 0}
      <section class="report-card">
        <h3>Recommended Actions</h3>
        <div class="report-actions-list">
          {#each actions as action, index (`action-${index}`)}
            {@const priority = getPriority(action)}
            <label class="report-action-row">
              <input type="checkbox" />
              <Widget type="status" value={priority} />
              <strong>{getActionTitle(action)}</strong>
            </label>
          {/each}
        </div>
      </section>
    {/if}
  </div>
{/if}
